import { USD } from "@dinero.js/currencies";
import { dinero } from "dinero.js";
import { compact } from "lodash";
import { z } from "zod";
import { BaseConnector } from "../baseConnector";
export const websiteCodes = {
    arrow: "arrow.com",
    verical: "verical.com",
};
/**
 * @see https://developers.arrow.com/api/index.php/site/page?view=v4isSearchToken
 */
export class ArrowConnector extends BaseConnector {
    websiteCode = "arrow";
    getApiKey;
    constructor({ getApiKey }) {
        super();
        this.getApiKey = getApiKey;
    }
    get baseUrl() {
        return `http://api.arrow.com/itemservice/v4/en/search/token`;
    }
    async search(params) {
        let response;
        if ("mpn" in params) {
            response = await this.searchByKeyword(params.mpn);
        }
        else if ("keyword" in params) {
            response = await this.searchByKeyword(params.keyword);
        }
        else {
            throw new Error("Invalid search params");
        }
        return processSearchResponse(this.websiteCode, response, params.manufacturer);
    }
    /**
     * QUESTION: better implementation using built-in manufacturer filter? See:
     * https://developers.arrow.com/api/index.php/site/page?view=v1isLookupMfr
     *
     * Unfortunately, the API does not support multiple keywords.
     */
    async searchByKeyword(keyword) {
        const res = await this.get("", {
            params: {
                login: "flux1",
                apiKey: this.getApiKey(),
                search_token: keyword,
                rows: 25,
            },
        });
        return ArrowSearchResponseSchema.parse(res.payload);
    }
}
function processSearchResponse(websiteCode, response, manufacturer = "") {
    const transactionArea = response.itemserviceresult.transactionArea;
    if (!transactionArea)
        throw new Error("No transactionArea found in response.");
    if (transactionArea.length !== 1)
        throw new Error("Expected exactly one transactionArea in response.");
    // NOTE: Arrow always returns 200 status code, even when there is an error.
    const tResponse = transactionArea[0].response;
    if (tResponse.success === false) {
        // NOTE: Arrow returns this "error" when there are no results
        if (tResponse.returnCode === "unsuccessful result" && tResponse.returnMsg === "no results") {
            return [];
        }
        throw new Error(`Arrow Error ${tResponse.returnCode}: ${tResponse.returnMsg}`);
    }
    // NOTE: cast to ArrowSearchResultsSchema here because the `success ===
    // false` discrimination is not working :/
    const datas = response.itemserviceresult.data;
    return datas.flatMap((data) => data.PartList.flatMap((part) => {
        const websites = part.InvOrg.webSites;
        // QUESTION: support verical.com also? see
        // [workplace-post/468318672455203](/docs/workplace/468318672455203.md)
        const sources = websites.find(
        // NOTE: incredibly, the API returns 'arrow.com' and 'Verical.com'
        (website) => website.code.toLowerCase() === websiteCodes[websiteCode].toLowerCase())?.sources;
        if (!sources) {
            return [];
        }
        if (manufacturer) {
            // QUESTION: broaden match better somehow?
            manufacturer = manufacturer.toLowerCase();
            if (manufacturer != part.manufacturer.mfrName.toLowerCase() &&
                manufacturer != part.manufacturer.mfrCd.toLowerCase()) {
                return [];
            }
        }
        return sources.flatMap((source) => source.sourceParts.map((sourcePart) => {
            const result = {
                distributor: websiteCode,
                mpn: part.partNum,
                dpn: sourcePart.sourcePartId,
                availableQuantity: sourcePart.Availability[0].fohQty,
                minimumOrderQuantity: sourcePart.minimumOrderQuantity,
                datasheetUrl: part.hasDatasheet
                    ? part.resources.find((r) => r.type === "datasheet")?.uri ?? null
                    : null,
                description: part.desc,
                leadTimeDays: extractArrowLeadTime(sourcePart),
                lifecycleStatus: part.status,
                manufacturerName: part.manufacturer.mfrName,
                packagingOptions: compact([part.packageType]), // QUESTION: why is this empty when the detail page has it?
                priceBreaks: (sourcePart.Prices?.resaleList ?? []).map((pb) => ({
                    quantity: pb.minQty,
                    price: dinero({ amount: Math.round(pb.price * 100000), scale: 5, currency: USD }),
                    currency: sources[0].currency,
                })),
                listingUrl: sourcePart.resources.find((r) => r.type === "detail")?.uri,
                distributorAttributes: {
                    distributor: websiteCode,
                    Category: part.categoryName,
                    // QUESTION: fill in more arbitrary attributes?
                },
            };
            return result;
        }));
    }));
}
const ArrowSearchErrorSchema = z.object({
    itemserviceresult: z.object({
        transactionArea: z.array(z.object({
            response: z.object({
                returnCode: z.string(),
                returnMsg: z.string(),
                success: z.literal(false),
            }),
        })),
    }),
});
const ArrowSourcePartSchema = z.object({
    packSize: z.number(),
    minimumOrderQuantity: z.number(),
    sourcePartId: z.string(),
    Prices: z
        .object({
        resaleList: z.array(z.object({
            displayPrice: z.string(),
            price: z.number(),
            minQty: z.number(),
            maxQty: z.number(),
        })),
    })
        // NOTE: pricing is missing when availability is "Call For Quote"
        .optional(),
    Availability: z.array(z.object({
        fohQty: z.number(), // Free On Hand Quantity, i.e. available inventory.
        availabilityCd: z.string(),
        availabilityMessage: z.string(),
        pipeline: z.array(z.unknown()),
    })),
    customerSpecificPricing: z.array(z.unknown()),
    customerSpecificInventory: z.array(z.unknown()),
    dateCode: z.string(),
    resources: z.array(z.object({
        type: z.string(),
        uri: z.string(),
    })),
    inStock: z.boolean(),
    mfrLeadTime: z.number(), // Lead time from Manufacturer. Listed in weeks
    tariffFlag: z.boolean(),
    shipsFrom: z.string(),
    shipsIn: z.string(), // number of days the item will be shipped in
    arrowLeadTime: z.string(), // Arrow's lead time for the part in weeks
    isNcnr: z.boolean(),
    isNpi: z.boolean(),
    productCode: z.string(), // Compressed part number
    containerType: z.string(),
});
const ArrowSearchResultsSchema = z.object({
    itemserviceresult: z.object({
        transactionArea: z.array(z.object({
            response: z.object({
                returnCode: z.string(),
                returnMsg: z.string(),
                success: z.literal(true),
            }),
        })),
        data: z.array(z.object({
            resources: z.array(z.object({})),
            PartList: z.array(z.object({
                itemId: z.number(),
                partNum: z.string(),
                manufacturer: z.object({
                    mfrCd: z.string(),
                    mfrName: z.string(),
                }),
                desc: z.string(),
                packageType: z.string(),
                // NOTE: this three are out of order with examples, for readability
                hasDatasheet: z.boolean(),
                categoryName: z.string(),
                status: z.string(),
                resources: z.array(z.object({
                    type: z.string(),
                    uri: z.string(),
                })),
                InvOrg: z.object({
                    webSites: z.array(z.object({
                        code: z.string(),
                        name: z.string(),
                        sources: z.array(z.object({
                            currency: z.string(),
                            sourceCd: z.string(),
                            displayName: z.string(),
                            sourceParts: z.array(ArrowSourcePartSchema),
                        })),
                    })),
                }),
            })),
        })),
    }),
});
const ArrowSearchResponseSchema = z.union([ArrowSearchErrorSchema, ArrowSearchResultsSchema]);
export function extractArrowLeadTime(sourcePart) {
    const match = sourcePart.shipsIn.match(/\d+/);
    if (match) {
        return parseInt(match[0]);
    }
    // NOTE: arrowLeadTime seems to always be empty
    const arrowLeadTime = Number(sourcePart.arrowLeadTime);
    if (arrowLeadTime) {
        return arrowLeadTime * 7;
    }
    // QUESTION: is it a good idea to use mfrLeadTime as a fallback when it is
    // often 10 weeks or more?
    return sourcePart.mfrLeadTime ? sourcePart.mfrLeadTime * 7 : null;
}
//# sourceMappingURL=arrow.js.map